import {
  MotionValue,
  SpringOptions,
  useAnimationFrame,
  useMotionValue,
  useSpring,
} from 'motion/react';
import { useEffect, useMemo, useRef } from 'react';

export interface MouseCoords<T = number> {
  x: T;
  y: T;
}

export const useMousePosition = () => {
  const ref = useRef<MouseCoords>({ x: 0, y: 0 });

  useEffect(() => {
    const updateMousePosition = (event: MouseEvent) => {
      ref.current.x = event.clientX;
      ref.current.y = event.clientY;
    };

    window.addEventListener('mousemove', updateMousePosition);

    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, []);

  return ref;
};

export const useCenteredMousePosition = () => {
  const ref = useRef<MouseCoords>({ x: 0, y: 0 });

  useEffect(() => {
    const updateMousePosition = (e: MouseEvent) => {
      const { innerWidth: w, innerHeight: h } = window;

      const x = (e.clientX - w / 2) / (w / 2);
      const y = (h / 2 - e.clientY) / (h / 2);

      ref.current.x = x;
      ref.current.y = y;
    };

    window.addEventListener('mousemove', updateMousePosition);

    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, []);

  return ref;
};

export const defaultSpring: SpringOptions = {
  visualDuration: 0.5,
  bounce: 0.25,
};

export interface UseMotionMousePositionOptions {
  offset?: MouseCoords;
  spring?: SpringOptions;
  disabled?: boolean;
}

export type UseMotionMousePositionValue = [
  MotionValue<number>,
  MotionValue<number>,
] &
  MouseCoords<MotionValue<number>>;

export const useMotionMousePosition = ({
  offset = { x: 0, y: 0 },
  spring = defaultSpring,
  disabled = false,
}) => {
  const mousePositionRef = useMousePosition();

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  const x = useSpring(rawX, spring);
  const y = useSpring(rawY, spring);

  useAnimationFrame(() => {
    if (!disabled && mousePositionRef.current) {
      rawX.set(mousePositionRef.current.x + offset.x);
      rawY.set(mousePositionRef.current.y + offset.y);
    }
  });

  return useMemo<UseMotionMousePositionValue>(() => {
    const value = [x, y] as UseMotionMousePositionValue;

    value.x = x;
    value.y = y;

    return value;
  }, [x, y]);
};
